import { TAX_RELIEF_BASIC_RATE_MULTIPLIER } from './utils/calculationUtils';

/**
 * Calculates the pension deduction using configuration values.
 *
 * @param {number} salary - The user's base salary.
 * @param {number|string} pensionContribution - The user's pension contribution.
 * @param {("percentage"|"amount")} pensionInputType - The input type.
 * @param {string} pensionScheme - The selected pension scheme.
 * @param {string} calculationBasis - "Gross Salary" or "Qualifying Earnings".
 * @param {boolean} includeOvertime - Whether overtime is included.
 * @param {number} overtimeAddition - Additional income from overtime.
 * @param {Object} config - The tax year configuration object.
 *
 * @returns {number} - The calculated pension deduction.
 */
export const calculatePensionDeduction = (
  salary,
  pensionContribution,
  pensionInputType,
  pensionScheme,
  calculationBasis,
  includeOvertime,
  overtimeAddition = 0,
  config
) => {
  // Safety check for config - requires PENSIONS.QUALIFYING_EARNINGS structure
  // Note: Full config validation is done in calculateAll() via validateConfig()
  if (!config || !config.PENSIONS?.QUALIFYING_EARNINGS) {
    return 0;
  }

  const qualifyingEarnings = config.PENSIONS.QUALIFYING_EARNINGS;
  let lowerThreshold, upperThreshold;

  if (calculationBasis === "Qualifying Earnings") {
    // Use qualifying earnings thresholds from config
    lowerThreshold = qualifyingEarnings.LOWER_THRESHOLD;
    upperThreshold = qualifyingEarnings.UPPER_THRESHOLD;
  } else {
    // Gross salary basis - no thresholds
    lowerThreshold = 0;
    upperThreshold = salary;
  }

  let pensionableIncome;
  // Apply the "Qualifying Earnings" logic only if the scheme is one of the recognized ones.
  if (
    (pensionScheme === "Auto-enrolment" ||
      pensionScheme === "Employer/Occupational" ||
      pensionScheme === "Salary Sacrifice" ||
      pensionScheme === "Personal") &&
    calculationBasis === "Qualifying Earnings"
  ) {
    const effectiveSalary = includeOvertime
      ? Math.min(salary + overtimeAddition, upperThreshold)
      : salary;
    pensionableIncome = Math.max(0, Math.min(effectiveSalary, upperThreshold) - lowerThreshold);
  } else {
    // Fallback: use the gross salary (plus overtime if included)
    pensionableIncome = includeOvertime ? salary + overtimeAddition : salary;
  }

  // Calculate the base pension contribution.
  let baseContribution = pensionInputType === "percentage"
    ? pensionableIncome * (Number(pensionContribution) / 100)
    : Number(pensionContribution);

  // For Personal pension contributions entered as an amount (net contribution),
  // return the gross contribution by boosting it at the basic rate.
  if (pensionScheme === "Personal" && pensionInputType === "amount") {
    return baseContribution * TAX_RELIEF_BASIC_RATE_MULTIPLIER;
  }

  return baseContribution;
};
